import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Heart, TrendingUp, Clock, ExternalLink } from 'lucide-react';
import { dreamService, type DreamIdea } from '@/services/dreamService';
import { toast } from 'sonner';

type SortOption = 'newest' | 'votes' | 'trending';

export const DreamBoard = () => {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedDream, setSelectedDream] = useState<DreamIdea | null>(null);
  const [votedDreams, setVotedDreams] = useState<Set<string>>(new Set());

  const { data: dreams = [], refetch } = useQuery({
    queryKey: ['dreams'],
    queryFn: dreamService.getAllDreams,
  });

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = dreamService.subscribe(() => {
      console.log('🔄 Dreams updated, refetching...');
      refetch();
    });
    return unsubscribe;
  }, [refetch]);

  const sortedDreams = [...dreams].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'votes':
        return b.votes - a.votes;
      case 'trending':
        // Trending: combination of votes and recency
        if (a.trending && !b.trending) return -1;
        if (!a.trending && b.trending) return 1;
        return b.votes - a.votes;
      default:
        return 0;
    }
  });

  const handleVote = async (dreamId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (votedDreams.has(dreamId)) {
      toast.info('You already voted for this idea!');
      return;
    }

    await dreamService.voteDream(dreamId);
    setVotedDreams(prev => new Set([...prev, dreamId]));
    toast.success('Vote recorded! ❤️');
    refetch();
  };

  const getTeaser = (description: string) => {
    return description.length > 80 ? description.substring(0, 80) + '...' : description;
  };

  return (
    <section className="py-24 px-6 bg-background/50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            The Dream Board
          </h2>
          <p className="text-lg text-muted-foreground">Latest SaaS Ideas from the Community</p>
        </div>

        {/* Sort Filter Bar */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <Button
            variant={sortBy === 'newest' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('newest')}
            className={sortBy === 'newest' ? 'bg-primary' : 'border-border/50'}
          >
            <Clock className="w-4 h-4 mr-2" />
            Newest
          </Button>
          <Button
            variant={sortBy === 'votes' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('votes')}
            className={sortBy === 'votes' ? 'bg-secondary' : 'border-border/50'}
          >
            <Heart className="w-4 h-4 mr-2" />
            Most Votes
          </Button>
          <Button
            variant={sortBy === 'trending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('trending')}
            className={sortBy === 'trending' ? 'bg-accent text-accent-foreground' : 'border-border/50'}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Trending
          </Button>
        </div>

        {/* Dream Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedDreams.map((dream) => (
            <Card
              key={dream.id}
              className="group relative p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-accent/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(96,165,250,0.3)] cursor-pointer"
              onClick={() => setSelectedDream(dream)}
            >
              {/* Trending Badge */}
              {dream.trending && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-gradient-to-r from-primary to-accent text-white border-0">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Trending
                  </Badge>
                </div>
              )}

              {/* Title */}
              <h3 className="text-xl font-bold mb-2 text-foreground group-hover:text-accent transition-colors line-clamp-2 pr-8">
                {dream.title}
              </h3>

              {/* Submitted By */}
              <p className="text-sm text-muted-foreground mb-4">
                Submitted by{' '}
                <span className="text-accent font-medium">
                  {dream.twitterHandle || dream.name}
                </span>
              </p>

              {/* Teaser */}
              <p className="text-sm text-foreground/70 mb-4 line-clamp-2">
                {getTeaser(dream.description)}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {dream.tags.map((tag, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-xs border-primary/30 text-primary"
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-border/30">
                <button
                  onClick={(e) => handleVote(dream.id, e)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                    votedDreams.has(dream.id)
                      ? 'bg-destructive/20 text-destructive'
                      : 'hover:bg-destructive/10 text-muted-foreground hover:text-destructive'
                  }`}
                >
                  <Heart
                    className={`w-4 h-4 ${votedDreams.has(dream.id) ? 'fill-current' : ''}`}
                  />
                  <span className="text-sm font-semibold">{dream.votes}</span>
                </button>

                <Button
                  size="sm"
                  variant="ghost"
                  className="text-accent hover:text-accent hover:bg-accent/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDream(dream);
                  }}
                >
                  Read Full Pitch →
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {sortedDreams.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg text-muted-foreground">
              No dreams submitted yet. Be the first to share your idea! 🚀
            </p>
          </div>
        )}
      </div>

      {/* Dream Detail Modal */}
      <Dialog open={!!selectedDream} onOpenChange={() => setSelectedDream(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-accent/30">
          {selectedDream && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent pr-8">
                  {selectedDream.title}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  By {selectedDream.twitterHandle || selectedDream.name} •{' '}
                  {new Date(selectedDream.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Tags & Votes */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {selectedDream.tags.map((tag, idx) => (
                      <Badge
                        key={idx}
                        className="bg-primary/20 text-primary border-primary/30"
                      >
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVote(selectedDream.id, e);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      votedDreams.has(selectedDream.id)
                        ? 'bg-destructive/20 text-destructive'
                        : 'hover:bg-destructive/10 text-muted-foreground hover:text-destructive'
                    }`}
                  >
                    <Heart
                      className={`w-5 h-5 ${votedDreams.has(selectedDream.id) ? 'fill-current' : ''}`}
                    />
                    <span className="font-semibold">{selectedDream.votes}</span>
                  </button>
                </div>

                {/* Mockup Image */}
                {selectedDream.mockupUrl && (
                  <div className="rounded-lg overflow-hidden border border-border/30">
                    <img
                      src={selectedDream.mockupUrl}
                      alt="Mockup"
                      className="w-full h-auto"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Description */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">
                    {selectedDream.description}
                  </p>
                </div>

                {/* Video Link */}
                {selectedDream.videoUrl && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <a
                      href={selectedDream.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                      <span className="font-medium">Watch Video Pitch</span>
                    </a>
                  </div>
                )}

                {/* Share on X */}
                <div className="pt-4 border-t border-border/30">
                  <Button
                    onClick={() => {
                      const text = `Check out this amazing SaaS idea: "${selectedDream.title}" on Dream SaaS Empire! #DreamSaaSEmpire @layout_dev`;
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
                        '_blank'
                      );
                    }}
                    className="w-full bg-gradient-to-r from-primary via-secondary to-accent"
                  >
                    Share on X
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
